import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Repeat, CheckCircle, Loader2 } from 'lucide-react';
import { mockEvents } from '../data/mock';
import { contactApi, eventsApi } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import EventCalendar from './EventCalendar';

const Events = () => {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMsg, setNewsletterMsg] = useState(null);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await eventsApi.getAll(true);
        if (data && data.length > 0) {
          setEvents(data);
        } else {
          setEvents(mockEvents);
        }
      } catch {
        setEvents(mockEvents);
      } finally {
        setEventsLoading(false);
      }
    };
    loadEvents();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterLoading(true);
    setNewsletterMsg(null);
    try {
      const result = await contactApi.subscribeNewsletter(newsletterEmail);
      setNewsletterMsg({ type: 'success', text: result.message });
      setNewsletterEmail('');
    } catch {
      setNewsletterMsg({ type: 'error', text: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.' });
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <section id="events" className="py-24 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-14">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {t('events.title')}
          </h2>
          <div className="w-20 h-1 bg-[#0f2440] mx-auto mb-6"></div>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('events.subtitle')}
          </p>
        </div>

        {/* Event Calendar + Weekly Schedule */}
        {eventsLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0f2440]" />
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            {/* Calendar View */}
            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              <div className="lg:col-span-1">
                <EventCalendar events={events} />
              </div>
              <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {events.map((event) => (
              <div
                key={event.id}
                data-testid={`event-card-${event.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl 
                           transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                {/* Color Top Bar */}
                <div className="h-1.5" style={{ backgroundColor: event.color || '#3b82f6' }}></div>

                {/* Event Image */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={event.image_url || event.image || 'https://images.unsplash.com/photo-1698768676413-e49b729dafa6?w=800'}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent"></div>
                  {/* Day Badge */}
                  <div className="absolute bottom-3 left-3">
                    <span 
                      className="px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg"
                      style={{ backgroundColor: event.color || '#3b82f6' }}
                    >
                      {event.time}
                    </span>
                  </div>
                </div>

                {/* Event Details */}
                <div className="p-5 space-y-3">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#0f2440] transition-colors">
                    {event.title}
                  </h3>
                  
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                    {event.description}
                  </p>

                  <div className="space-y-2 text-sm pt-1">
                    <div className="flex items-center gap-2 text-gray-500">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                      <span className="truncate text-xs">Am Klagesmarkt 29</span>
                    </div>

                    <div className="flex items-center gap-2 font-medium" style={{ color: event.color || '#3b82f6' }}>
                      <Repeat className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="text-xs">{event.recurring}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
              </div>
            </div>
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-16 max-w-4xl mx-auto bg-[#0f2440] rounded-3xl p-8 md:p-12 text-center text-white shadow-2xl">
          <Calendar className="w-14 h-14 mx-auto mb-5 text-sky-300" />
          <h3 className="text-2xl md:text-3xl font-bold mb-3">
            {t('events.newsletter')}
          </h3>
          <p className="text-base text-white/80 mb-8 max-w-lg mx-auto">
            {t('events.newsletterSub')}
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            <input
              type="email"
              placeholder={t('events.emailPlaceholder')}
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              required
              data-testid="events-newsletter-email"
              className="flex-1 px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-4 
                         focus:ring-sky-300/30"
            />
            <button 
              type="submit"
              disabled={newsletterLoading}
              data-testid="events-newsletter-submit"
              className="px-8 py-3 bg-white text-[#0f2440] rounded-lg font-semibold 
                         hover:bg-sky-50 transition-all hover:scale-105 whitespace-nowrap
                         disabled:opacity-50 disabled:cursor-not-allowed">
              {newsletterLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t('events.subscribe')}
            </button>
          </form>
          {newsletterMsg && (
            <div data-testid="events-newsletter-message" className={`mt-4 text-sm flex items-center justify-center gap-2 ${newsletterMsg.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
              {newsletterMsg.type === 'success' && <CheckCircle className="w-4 h-4" />}
              {newsletterMsg.text}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Events;
